import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, faClock, faInfoCircle, faRibbon, faQuestionCircle, 
  faArrowRight, faUndo, faShieldAlt, faHeart, faBan, faTimesCircle 
} from '@fortawesome/free-solid-svg-icons';

// Data model for contraception methods
const CONTRACEPTIVE_METHODS = [
  {
    id: 'DIU',
    name: 'DIU (Stérilet au cuivre ou hormonal)',
    efficiency: '99.2% à 99.8%',
    duration: '3 à 10 ans',
    involvement: 'Zéro contrainte (Poser et oublier)',
    hormones: 'Disponible avec ou sans hormones',
    stiProtect: false,
    description: 'Un petit dispositif en forme de T inséré dans l\'utérus par un professionnel de santé. Très efficace et réversible immédiatement après retrait.',
    pros: ['Tranquillité d\'esprit pendant plusieurs années', 'Réversible à tout moment', 'Option sans hormones (DIU cuivre)'],
    cons: ['Pose parfois inconfortable', 'Peut modifier la durée des règles (règles plus abondantes avec le cuivre)'],
    type: 'DIU',
    scoreRules: (answers) => {
      let score = 50;
      if (answers.involvement === 'zero') score += 40;
      if (answers.duration === 'long') score += 20;
      if (answers.hormones === 'no' && answers.involvement === 'zero') score += 10;
      return Math.min(score, 99);
    }
  },
  {
    id: 'IMPLANT',
    name: 'Implant sous-cutané',
    efficiency: 'Plus de 99.9%',
    duration: '3 ans',
    involvement: 'Zéro contrainte (Poser et oublier)',
    hormones: 'Hormonal',
    stiProtect: false,
    description: 'Un petit bâtonnet souple de la taille d\'une allumette, inséré sous la peau du bras par un médecin. Il libère des hormones en continu.',
    pros: ['Le moyen de contraception le plus efficace disponible', 'Aucun oubli possible', 'Dure 3 ans'],
    cons: ['Hormonal (effets secondaires possibles)', 'Nécessite une petite incision pour la pose et le retrait'],
    type: 'IMPLANT',
    scoreRules: (answers) => {
      let score = 50;
      if (answers.involvement === 'zero') score += 40;
      if (answers.duration === 'long' || answers.duration === 'medium') score += 20;
      if (answers.hormones === 'yes') score += 10;
      return Math.min(score, 99);
    }
  },
  {
    id: 'PILULE',
    name: 'Pilule contraceptive',
    efficiency: '91% (utilisation courante) à 99.7% (utilisation parfaite)',
    duration: 'Quotidienne (à prendre chaque jour)',
    involvement: 'Quotidien (rigueur exigée)',
    hormones: 'Hormonal',
    stiProtect: false,
    description: 'Comprimé contenant des hormones à prendre à heure fixe chaque jour. Nécessite une prescription médicale.',
    pros: ['Régule les cycles et diminue les douleurs menstruelles', 'Facile à arrêter en cas de projet de grossesse', 'Très répandue'],
    cons: ['Risque d\'oubli important', 'Doit être prise à heure fixe', 'Effets secondaires hormonaux potentiels'],
    type: 'PILULE',
    scoreRules: (answers) => {
      let score = 55;
      if (answers.involvement === 'daily') score += 35;
      if (answers.duration === 'short') score += 15;
      if (answers.hormones === 'yes') score += 10;
      return Math.min(score, 98);
    }
  },
  {
    id: 'PRESERVATIF',
    name: 'Préservatif (Masculin ou Féminin)',
    efficiency: '85% (utilisation courante) à 98% (utilisation parfaite)',
    duration: 'À chaque rapport sexuel',
    involvement: 'À chaque rapport',
    hormones: 'Sans hormones',
    stiProtect: true,
    description: 'Une barrière en latex ou polyuréthane. C\'est le seul moyen de contraception qui protège simultanément contre les IST et le VIH.',
    pros: ['Protège contre les Infections Sexuellement Transmissibles (IST)', 'Disponible sans ordonnance', 'Aucun effet secondaire hormonal'],
    cons: ['Nécessite d\'interrompre le rapport pour le poser', 'Peut se déchirer ou glisser s\'il est mal utilisé'],
    type: 'PRESERVATIF',
    scoreRules: (answers) => {
      let score = 40;
      if (answers.sti === 'yes') score += 59; // Essential if STI protection is required
      if (answers.involvement === 'rapport') score += 30;
      if (answers.hormones === 'no') score += 20;
      return Math.min(score, 99);
    }
  },
  {
    id: 'INJECTION',
    name: 'Injection contraceptive',
    efficiency: '94% (utilisation courante) à 99.8% (utilisation parfaite)',
    duration: 'Toutes les 12 semaines (3 mois)',
    involvement: 'Trimestriel',
    hormones: 'Hormonal',
    stiProtect: false,
    description: 'Une injection intramusculaire d\'hormones pratiquée par un professionnel de santé tous les 3 mois.',
    pros: ['Une seule injection tous les 3 mois', 'Discrète', 'Efficace'],
    cons: ['Nécessite de se rendre chez le médecin/infirmier tous les 3 mois', 'Le retour à la fertilité peut prendre quelques mois après l\'arrêt'],
    type: 'INJECTION',
    scoreRules: (answers) => {
      let score = 50;
      if (answers.involvement === 'weekly_monthly') score += 35;
      if (answers.duration === 'medium' || answers.duration === 'short') score += 15;
      if (answers.hormones === 'yes') score += 10;
      return Math.min(score, 95);
    }
  },
  {
    id: 'METHODE_NATURELLE',
    name: 'Méthodes d\'auto-observation (Calendrier, Billings, Température)',
    efficiency: '76% à 88% (dépend de la rigueur)',
    duration: 'Continue',
    involvement: 'Observation quotidienne',
    hormones: 'Sans hormones',
    stiProtect: false,
    description: 'Consiste à identifier la période fertile (via les calculs de cycle, la glaire cervicale ou la température corporelle) pour éviter les rapports non protégés ces jours-là.',
    pros: ['100% naturelle', 'Gratuite et sans effets secondaires chimiques', 'Aide à mieux connaître son corps'],
    cons: ['Efficacité faible en cas de cycles irréguliers', 'Nécessite une abstinence ou l\'usage de préservatifs pendant la période fertile', 'Contrainte d\'observation quotidienne'],
    type: 'METHODE_NATURELLE',
    scoreRules: (answers) => {
      let score = 30;
      if (answers.involvement === 'natural') score += 50;
      if (answers.hormones === 'no') score += 20;
      if (answers.objectif === 'sante') score += 10;
      return Math.min(score, 90);
    }
  }
];

export default function PlanningFamilialSimulator() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    objectif: '',
    duration: '',
    involvement: '',
    hormones: '',
    sti: ''
  });
  const [results, setResults] = useState([]);

  const handleSelectOption = (key, value) => {
    const updatedAnswers = { ...answers, [key]: value };
    setAnswers(updatedAnswers);
    
    if (step < 4) {
      setStep(step + 1);
    } else {
      // Calculate scores and generate recommendations
      const scoredMethods = CONTRACEPTIVE_METHODS.map(method => {
        const score = method.scoreRules(updatedAnswers);
        return { ...method, score };
      }).sort((a, b) => b.score - a.score);
      setResults(scoredMethods);
      setStep(5); // Show results
    }
  };

  const handleRestart = () => {
    setAnswers({
      objectif: '',
      duration: '',
      involvement: '',
      hormones: '',
      sti: ''
    });
    setStep(0);
    setResults([]);
  };

  const renderProgress = () => {
    if (step > 4) return null;
    return (
      <div className="w-full bg-slate-100 rounded-full h-1.5 mb-8 overflow-hidden">
        <div 
          className="bg-gradient-to-r from-teal-500 to-indigo-500 h-1.5 transition-all duration-300"
          style={{ width: `${(step / 5) * 100}%` }}
        ></div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-slate-50 to-teal-50/50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-500 mb-4 shadow-sm">
            <FontAwesomeIcon icon={faShieldAlt} className="text-3xl" />
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-800 tracking-tight mb-4">
            Simulateur de Planning Familial
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
            Trouvez les méthodes de planning familial les plus adaptées à vos besoins et à votre style de vie en répondant à 5 questions rapides.
          </p>
        </div>

        {/* Wizard Container */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-xl p-6 sm:p-10 transition-all duration-300">
          {renderProgress()}

          {/* Question 1: Objectif */}
          {step === 0 && (
            <div className="animate__animated animate__fadeIn">
              <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center text-sm font-bold">1</span>
                Quel est votre objectif principal actuel ?
              </h2>
              <p className="text-slate-500 text-sm mb-6">Sélectionnez la raison pour laquelle vous envisagez le planning familial.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={() => handleSelectOption('objectif', 'contraception')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faBan} className="text-xl text-rose-500 mb-3" />
                  <div className="font-bold text-slate-800 text-base mb-1">Éviter une grossesse</div>
                  <div className="text-xs text-slate-500">Je cherche une contraception efficace.</div>
                </button>
                <button 
                  onClick={() => handleSelectOption('objectif', 'procreation')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faHeart} className="text-xl text-pink-500 mb-3" />
                  <div className="font-bold text-slate-800 text-base mb-1">Planifier une grossesse</div>
                  <div className="text-xs text-slate-500">Je souhaite concevoir un enfant à l'avenir.</div>
                </button>
                <button 
                  onClick={() => handleSelectOption('objectif', 'sante')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="text-xl text-teal-500 mb-3" />
                  <div className="font-bold text-slate-800 text-base mb-1">Suivre ma santé</div>
                  <div className="text-xs text-slate-500">Mieux observer mes cycles et comprendre mon corps.</div>
                </button>
              </div>
            </div>
          )}

          {/* Question 2: Durée */}
          {step === 1 && (
            <div className="animate__animated animate__fadeIn">
              <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center text-sm font-bold">2</span>
                Sur quelle durée prévoyez-vous cette méthode ?
              </h2>
              <p className="text-slate-500 text-sm mb-6">Cela permet de choisir entre des méthodes temporaires ou à longue durée d'action.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button 
                  onClick={() => handleSelectOption('duration', 'short')}
                  className="p-5 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-800">Court terme (&lt; 1 an)</div>
                    <div className="text-xs text-slate-500">Méthodes souples, faciles à arrêter rapidement.</div>
                  </div>
                </button>
                <button 
                  onClick={() => handleSelectOption('duration', 'medium')}
                  className="p-5 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-800">Moyen terme (1 à 3 ans)</div>
                    <div className="text-xs text-slate-500">Recherche d'une protection modérée dans le temps.</div>
                  </div>
                </button>
                <button 
                  onClick={() => handleSelectOption('duration', 'long')}
                  className="p-5 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-800">Long terme (3 ans et +)</div>
                    <div className="text-xs text-slate-500">Stérilets, implants, zéro préoccupation quotidienne.</div>
                  </div>
                </button>
                <button 
                  onClick={() => handleSelectOption('duration', 'permanent')}
                  className="p-5 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-500 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-800">Définitif / Permanent</div>
                    <div className="text-xs text-slate-500">Pas de projet d'enfant à l'avenir.</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Question 3: Implication */}
          {step === 2 && (
            <div className="animate__animated animate__fadeIn">
              <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center text-sm font-bold">3</span>
                Quel niveau d'implication souhaitez-vous au quotidien ?
              </h2>
              <p className="text-slate-500 text-sm mb-6">Choisissez la contrainte d'usage qui s'intègre le mieux à vos habitudes.</p>
              
              <div className="grid grid-cols-1 gap-3">
                <button 
                  onClick={() => handleSelectOption('involvement', 'zero')}
                  className="p-4 rounded-xl border border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/10 transition-all text-sm font-semibold text-slate-700 flex justify-between items-center"
                >
                  <span>Zéro contrainte (Implant ou stérilet longue durée, aucune action quotidienne)</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500" />
                </button>
                <button 
                  onClick={() => handleSelectOption('involvement', 'daily')}
                  className="p-4 rounded-xl border border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/10 transition-all text-sm font-semibold text-slate-700 flex justify-between items-center"
                >
                  <span>Quotidien (Prendre un comprimé tous les jours à la même heure)</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500" />
                </button>
                <button 
                  onClick={() => handleSelectOption('involvement', 'weekly_monthly')}
                  className="p-4 rounded-xl border border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/10 transition-all text-sm font-semibold text-slate-700 flex justify-between items-center"
                >
                  <span>Périodique (Faire une injection tous les 3 mois)</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500" />
                </button>
                <button 
                  onClick={() => handleSelectOption('involvement', 'rapport')}
                  className="p-4 rounded-xl border border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/10 transition-all text-sm font-semibold text-slate-700 flex justify-between items-center"
                >
                  <span>Au moment du rapport (Préservatif uniquement lors des rapports)</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500" />
                </button>
                <button 
                  onClick={() => handleSelectOption('involvement', 'natural')}
                  className="p-4 rounded-xl border border-slate-100 hover:border-indigo-500 text-left bg-slate-50 hover:bg-indigo-50/10 transition-all text-sm font-semibold text-slate-700 flex justify-between items-center"
                >
                  <span>Observation naturelle (Observer mes glaires, températures ou calendrier)</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500" />
                </button>
              </div>
            </div>
          )}

          {/* Question 4: Hormones */}
          {step === 3 && (
            <div className="animate__animated animate__fadeIn">
              <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center text-sm font-bold">4</span>
                Quelle est votre préférence par rapport aux hormones ?
              </h2>
              <p className="text-slate-500 text-sm mb-6">Certaines personnes préfèrent éviter les hormones pour des raisons médicales ou personnelles.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={() => handleSelectOption('hormones', 'yes')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-center bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <div className="font-bold text-slate-800 mb-1">Hormonale</div>
                  <div className="text-xs text-slate-500">Pilule, implant, injections...</div>
                </button>
                <button 
                  onClick={() => handleSelectOption('hormones', 'no')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-center bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <div className="font-bold text-slate-800 mb-1">Sans hormones</div>
                  <div className="text-xs text-slate-500">Préservatif, stérilet cuivre, naturel...</div>
                </button>
                <button 
                  onClick={() => handleSelectOption('hormones', 'any')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-center bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <div className="font-bold text-slate-800 mb-1">Peu importe</div>
                  <div className="text-xs text-slate-500">Toutes les options conviennent.</div>
                </button>
              </div>
            </div>
          )}

          {/* Question 5: Protection IST */}
          {step === 4 && (
            <div className="animate__animated animate__fadeIn">
              <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center text-sm font-bold">5</span>
                Avez-vous besoin de vous protéger contre les IST (SIDA, etc.) ?
              </h2>
              <p className="text-slate-500 text-sm mb-6">C'est une question cruciale car une seule méthode contraceptive bloque les maladies transmissibles.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button 
                  onClick={() => handleSelectOption('sti', 'yes')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-center bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faShieldAlt} className="text-3xl text-indigo-600 mb-3" />
                  <div className="font-bold text-slate-800 mb-1">Oui, indispensable</div>
                  <div className="text-xs text-slate-500">J'ai des partenaires multiples ou je ne connais pas leur statut.</div>
                </button>
                <button 
                  onClick={() => handleSelectOption('sti', 'no')}
                  className="p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 text-center bg-slate-50 hover:bg-indigo-50/20 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faRibbon} className="text-3xl text-pink-500 mb-3" />
                  <div className="font-bold text-slate-800 mb-1">Non, pas requis</div>
                  <div className="text-xs text-slate-500">Je suis en couple stable et exclusif et nous avons fait les tests.</div>
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Results */}
          {step === 5 && (
            <div className="animate__animated animate__fadeIn space-y-8">
              
              {/* Entête résultats */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pb-6 border-b border-slate-100">
                <div className="text-left">
                  <h2 className="text-2xl font-black text-slate-800">
                    Méthodes Recommandées
                  </h2>
                  <p className="text-slate-500 text-sm mt-1">Classées selon leur pourcentage de correspondance avec vos besoins.</p>
                </div>
                <button 
                  onClick={handleRestart}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all cursor-pointer flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faUndo} />
                  Recommencer le test
                </button>
              </div>

              {/* Alerte IST Critique */}
              {answers.sti === 'yes' && (
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-100 text-amber-800 text-sm leading-relaxed flex items-start gap-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-amber-500 mt-1 flex-shrink-0 text-base" />
                  <div>
                    <strong className="block mb-1">Attention : Protection IST Indispensable</strong>
                    Vous avez indiqué avoir besoin de vous protéger des IST. Seul le <strong className="text-indigo-900">Préservatif</strong> offre cette protection. Même si vous optez pour une méthode longue durée comme le stérilet ou l'implant pour éviter une grossesse, vous devez utiliser simultanément un préservatif à chaque rapport.
                  </div>
                </div>
              )}

              {/* Liste des recommandations */}
              <div className="space-y-6">
                {results.map((method, idx) => (
                  <div 
                    key={method.id}
                    className={`bg-white rounded-3xl border transition-all overflow-hidden flex flex-col md:flex-row ${idx === 0 ? 'border-indigo-200 shadow-md ring-4 ring-indigo-50/50' : 'border-slate-100 shadow-sm'}`}
                  >
                    {/* Score Match */}
                    <div className={`p-6 md:w-44 flex flex-col justify-center items-center text-center border-b md:border-b-0 md:border-r border-slate-100 ${idx === 0 ? 'bg-indigo-50/30' : 'bg-slate-50/30'}`}>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Correspondance</span>
                      <span className={`text-4xl font-black ${idx === 0 ? 'text-indigo-600' : 'text-slate-700'}`}>
                        {method.score}%
                      </span>
                      {idx === 0 && (
                        <span className="mt-2 px-2.5 py-1 bg-indigo-500 text-white rounded-full text-[10px] font-bold uppercase tracking-wider">
                          Idéal pour vous
                        </span>
                      )}
                    </div>

                    {/* Contenu */}
                    <div className="p-6 flex-1 space-y-4">
                      {/* Titre & Efficacité */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                        <h3 className="text-lg font-bold text-slate-800 m-0">
                          {method.name}
                        </h3>
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          Efficacité : {method.efficiency}
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {method.description}
                      </p>

                      {/* Propriétés */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-500 font-medium">
                        <div className="bg-slate-50 p-2 rounded-xl">
                          <span className="text-[10px] text-slate-400 block mb-0.5">Durée d'action</span>
                          <span className="text-slate-800">{method.duration}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded-xl">
                          <span className="text-[10px] text-slate-400 block mb-0.5">Implication</span>
                          <span className="text-slate-800">{method.involvement}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded-xl">
                          <span className="text-[10px] text-slate-400 block mb-0.5">Hormones</span>
                          <span className="text-slate-800">{method.hormones}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded-xl">
                          <span className="text-[10px] text-slate-400 block mb-0.5">Protection IST</span>
                          <span className={method.stiProtect ? 'text-indigo-600 font-bold' : 'text-rose-500'}>
                            {method.stiProtect ? 'Oui' : 'Non'}
                          </span>
                        </div>
                      </div>

                      {/* Avantages & Inconvénients */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs pt-2">
                        <div className="space-y-1.5">
                          <span className="font-bold text-slate-700 block mb-1 text-[11px] uppercase tracking-wide">Avantages :</span>
                          {method.pros.map((pro, index) => (
                            <div key={index} className="flex items-center gap-2 text-slate-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              <span>{pro}</span>
                            </div>
                          ))}
                        </div>
                        <div className="space-y-1.5">
                          <span className="font-bold text-slate-700 block mb-1 text-[11px] uppercase tracking-wide">Inconvénients :</span>
                          {method.cons.map((con, index) => (
                            <div key={index} className="flex items-center gap-2 text-slate-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                              <span>{con}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Final */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl p-6 sm:p-8 text-white text-center shadow-lg shadow-indigo-100">
                <h3 className="text-xl font-bold mb-2">Prêt à mettre en place votre planning familial ?</h3>
                <p className="text-sm text-indigo-100 max-w-lg mx-auto mb-6 leading-relaxed">
                  Créez un compte gratuitement sur YouthFP pour enregistrer votre planning, activer vos rappels de prise quotidiens et synchroniser votre méthode avec votre cycle menstruel.
                </p>
                <div className="flex gap-4 justify-center">
                  <a href="/register" className="bg-white text-indigo-600 hover:bg-slate-50 px-6 py-3 rounded-full font-bold text-sm shadow-md hover:shadow-lg transition-all no-underline">
                    Créer mon compte
                  </a>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
